import { useEffect, useState } from 'react';
import { useUpdateExtension } from '../../lib/api/hooks.js';
import { Section, Field, FormActions } from '../../components/ui/Form.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { ApiClientError } from '../../lib/api/client.js';
import { FeatureGate } from './FeatureGate.js';
import { FollowMeEditor } from './FollowMeEditor.js';
import type { Extension } from '@arcnet/contracts';

/**
 * Where this extension's calls go instead of, or after, ringing it.
 *
 * Forwarding and follow-me on the seat itself, each usable only while the seat's
 * type includes it. They were an account-wide list with nowhere to change a
 * forward, and a follow-me tab of their own -- two places for one question.
 */
export function ExtensionForwardingTab({ tenantId, ext, scope }: {
  tenantId: string; ext: Extension; scope: 'platform' | 'tenant';
}) {
  return (
    <>
      <FeatureGate tenantId={tenantId} extensionId={ext.id} scope={scope}
        feature="call_forwarding" name="Call forwarding">
        <ForwardingForm tenantId={tenantId} ext={ext} />
      </FeatureGate>
      <div style={{ marginTop: 18 }}>
        <FeatureGate tenantId={tenantId} extensionId={ext.id} scope={scope}
          feature="follow_me" name="Follow me">
          <FollowMeEditor tenantId={tenantId} ext={ext} />
        </FeatureGate>
      </div>
    </>
  );
}

/** Two to six digits: another seat on this account. See the hint for why. */
const ON_NET = /^\d{2,6}$/;

function ForwardingForm({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const save = useUpdateExtension(tenantId);
  const [always, setAlways] = useState('');
  const [busy, setBusy] = useState('');
  const [noAnswer, setNoAnswer] = useState('');

  useEffect(() => {
    setAlways(ext.forward_always ?? '');
    setBusy(ext.forward_busy ?? '');
    setNoAnswer(ext.forward_no_answer ?? '');
  }, [ext]);

  const dirty = always !== (ext.forward_always ?? '')
    || busy !== (ext.forward_busy ?? '')
    || noAnswer !== (ext.forward_no_answer ?? '');

  /**
   * Extension numbers only, said at the field.
   *
   * The switch acts on a forward to another seat; one to an outside number is
   * stored and then ignored, because it needs an outbound route and a carrier
   * that forwarding does not yet select. Accepting a mobile number here would be
   * a setting that looks saved and rings nobody -- the fault this screen exists
   * to prevent.
   */
  const invalid = (v: string) => v.trim() !== '' && !ON_NET.test(v.trim());
  const anyInvalid = [always, busy, noAnswer].some(invalid);
  const err = save.error instanceof ApiClientError ? save.error : null;
  const hint = 'Another extension on this account. Forwarding to outside numbers is not supported yet.';

  return (
    <>
      {save.error != null && !err && <ErrorBanner error={save.error} />}
      <Section title="Forwarding" description="Where calls go instead of ringing this extension, or when it does not answer.">
        <Field label="Always forward to" htmlFor="fw-always"
          {...(invalid(always) ? { error: hint } : { hint: 'Every call goes here and this extension does not ring.' })}>
          <input id="fw-always" className="arc-input arc-mono" inputMode="numeric" value={always}
            placeholder="1002" onChange={(e) => setAlways(e.target.value)} />
        </Field>
        <Field label="When busy" htmlFor="fw-busy"
          {...(invalid(busy) ? { error: hint } : { hint })}>
          <input id="fw-busy" className="arc-input arc-mono" inputMode="numeric" value={busy}
            onChange={(e) => setBusy(e.target.value)} />
        </Field>
        <Field label="When not answered" htmlFor="fw-noanswer"
          {...(invalid(noAnswer) ? { error: hint }
            : { hint: `After ${ext.call_timeout_sec} seconds of ringing.` })}>
          <input id="fw-noanswer" className="arc-input arc-mono" inputMode="numeric" value={noAnswer}
            onChange={(e) => setNoAnswer(e.target.value)} />
        </Field>
      </Section>
      <FormActions
        dirty={dirty && !anyInvalid} saving={save.isPending}
        error={err?.message ?? null}
        onReset={() => {
          setAlways(ext.forward_always ?? '');
          setBusy(ext.forward_busy ?? '');
          setNoAnswer(ext.forward_no_answer ?? '');
        }}
        onSave={() => save.mutate({
          id: ext.id,
          forward_always: always.trim() || null,
          forward_busy: busy.trim() || null,
          forward_no_answer: noAnswer.trim() || null,
        })}
      />
    </>
  );
}
